import { loadAudioDurationFromUrl } from '@src/services/audio';

// Fit zoom can show hundreds of blocks at once, so cap the fan-out.
const MAX_CONCURRENT = 6;
// FIFO cap, so a long session stays bounded.
const MAX_CACHED = 400;

// Module-level, so remounting reuses the work. pendingCache holds the requests
// still in flight; durationCache only settled values.
const durationCache = new Map();
const pendingCache = new Map();

const cacheDuration = (url, seconds) => {
  durationCache.set(url, seconds);
  if (durationCache.size > MAX_CACHED) {
    durationCache.delete(durationCache.keys().next().value);
  }
};

// Resolves 0 on error, never rejects.
const measureDuration = (url) => {
  if (durationCache.has(url)) return Promise.resolve(durationCache.get(url));
  if (pendingCache.has(url)) return pendingCache.get(url);

  const promise = loadAudioDurationFromUrl(url)
    .catch(() => 0)
    .then((seconds) => {
      cacheDuration(url, seconds);
      pendingCache.delete(url);
      return seconds;
    });
  pendingCache.set(url, promise);
  return promise;
};

// Workers drain one shared queue. `onSettle` fires after each measurement.
const measureAll = async (urls, onSettle) => {
  const queue = [...urls];
  const worker = async () => {
    while (queue.length) {
      // eslint-disable-next-line no-await-in-loop -- sequential per worker; concurrency comes from running several workers
      await measureDuration(queue.shift());
      onSettle?.();
    }
  };
  const workers = Array.from(
    { length: Math.min(MAX_CONCURRENT, queue.length) },
    worker,
  );
  await Promise.all(workers);
};

// Seconds for a measured url, or undefined if it has not settled.
const getMeasuredAudioDuration = (audioLink) =>
  audioLink ? durationCache.get(audioLink) : undefined;

export { measureAll, getMeasuredAudioDuration };
